import { DateTime } from 'luxon'
import currency from 'currency.js'
import {
  faMoneyCheckDollar,
  faReceipt,
  faCoins,
  faShapes,
} from '@fortawesome/pro-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { formatCurrency } from '@/utils/currencyHelpers'
import { Expense } from '@/actions/reportActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getCurrentUser } from '@/selectors/user.selectors'
import { getAllTransactionCategories } from './reports.selectors'
import { Text, Card, CardColors, Icon } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'

// The design's icon-block background (#353534) doesn't have a matching token in Icon's
// backgroundColor enum (closest, "black", is neutral-900 instead of neutral-700), so it's
// set directly via backgroundClassName, an override point the component already supports.
const SectionIcon = ({
  icon,
  className,
}: {
  icon: IconDefinition
  className?: string
}) => (
  <Icon
    icon={icon}
    backgroundStyle="rounded"
    backgroundClassName={`bg-neutral-700 ${className ?? ''}`}
    size="sm"
    color="text-moss-0"
  />
)

const SectionTotal = ({
  label,
  value,
  valueColor,
}: {
  label: string
  value: string
  valueColor: TextColors
}) => (
  <div className="flex items-baseline gap-2 whitespace-nowrap">
    <Text
      as={TypographyStyles.LABEL_SM}
      color={TextColors.MUTED}
      className="m-0 leading-none"
    >
      {label}
    </Text>
    <Text
      as={TypographyStyles.DISPLAY_XS}
      color={valueColor}
      className="m-0 leading-none"
    >
      {value}
    </Text>
  </div>
)

const LineItemRow = ({
  categoryId,
  amount,
  labelVariant,
  labelColor,
}: {
  categoryId: number | null
  amount: number | string
  labelVariant: TypographyStyles
  labelColor: TextColors
}) => {
  const allTransactionCategories = useReselector(getAllTransactionCategories)
  const categoryName =
    (categoryId !== null && allTransactionCategories[categoryId]?.name) ||
    'Uncategorized'

  return (
    <div className="flex items-center justify-between border-b border-neutral-100 py-4">
      <Text as={labelVariant} color={labelColor}>
        {categoryName}
      </Text>
      <Text as={TypographyStyles.LABEL_SM} color={TextColors.EMPHASIS}>
        {formatCurrency(amount)}
      </Text>
    </div>
  )
}

const ProfitAndLossPDF = ({
  startDate,
  endDate,
  expensesObj,
  profitsObj,
  otherObj,
}: {
  startDate: string | undefined
  endDate: string | undefined
  expensesObj: { expenses: Expense[]; sum: number | string } | null
  profitsObj: { profits: Expense[]; sum: number | string } | null
  otherObj?: { otherExpenses?: Expense[]; sum?: number | string } | null
}) => {
  const user = useReselector(getCurrentUser)

  if (!user) {
    return null
  }

  const netIncome = formatCurrency(
    currency(profitsObj?.sum || 0).add(expensesObj?.sum || 0)
  )

  return (
    <div
      style={{
        padding: '40px 56px',
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
      }}
    >
      <div className="flex items-start justify-between gap-[107px]">
        <Text as={TypographyStyles.H1}>Profit &amp; Loss Statement</Text>
        <div className="flex flex-col items-end gap-1 text-right">
          <Text as={TypographyStyles.EYEBROW}>Statement Period</Text>
          <Text as={TypographyStyles.BODY_MD} color={TextColors.EMPHASIS}>
            {startDate &&
              DateTime.fromFormat(startDate, DATE_FORMATS_LUXON.INPUT).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              )}{' '}
            to{' '}
            {endDate &&
              DateTime.fromFormat(endDate, DATE_FORMATS_LUXON.INPUT).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              )}
          </Text>
        </div>
      </div>

      <div className="flex flex-col gap-10">
        <Text as={TypographyStyles.DISPLAY_MD} className="m-0">
          {user.firstName} {user.lastName}
        </Text>

        {expensesObj && profitsObj && (
          <div className="flex flex-col gap-10">
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <SectionIcon
                    icon={faMoneyCheckDollar}
                    className="translate-y-[8px]"
                  />
                  <Text as={TypographyStyles.H1} className="m-0 leading-none">
                    Income
                  </Text>
                </div>
                <SectionTotal
                  label="Total Income"
                  value={formatCurrency(profitsObj.sum)}
                  valueColor={TextColors.EMPHASIS}
                />
              </div>
              <div className="flex flex-col pl-12">
                {profitsObj.profits.map((profit) => (
                  <LineItemRow
                    key={profit.transactionCategoryId}
                    categoryId={profit.transactionCategoryId}
                    amount={profit.sum}
                    labelVariant={TypographyStyles.BODY_MD}
                    labelColor={TextColors.HEADER}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <SectionIcon icon={faReceipt} className="translate-y-[8px]" />
                  <Text as={TypographyStyles.H1} className="m-0 leading-none">
                    Expenses
                  </Text>
                </div>
                <SectionTotal
                  label="Total Expenses"
                  value={formatCurrency(expensesObj.sum)}
                  valueColor={TextColors.EMPHASIS}
                />
              </div>
              <div className="flex flex-col pl-12">
                {expensesObj.expenses.map((expense) => (
                  <LineItemRow
                    key={expense.transactionCategoryId}
                    categoryId={expense.transactionCategoryId}
                    amount={expense.sum}
                    labelVariant={TypographyStyles.BODY_MD}
                    labelColor={TextColors.HEADER}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="border-t-2 border-neutral-200" />
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <SectionIcon icon={faCoins} className="translate-y-[8px]" />
                  <Text as={TypographyStyles.H1} className="m-0 leading-none">
                    Net Profit
                  </Text>
                </div>
                <div className="flex items-baseline gap-2 whitespace-nowrap">
                  <Text
                    as={TypographyStyles.LABEL_SM}
                    color={TextColors.MUTED}
                    className="m-0 leading-none"
                  >
                    Total
                  </Text>
                  <Text
                    as={TypographyStyles.DISPLAY_MD}
                    color={TextColors.HEADER}
                    className="m-0 leading-none"
                  >
                    {netIncome}
                  </Text>
                </div>
              </div>
            </div>

            {otherObj && (
              <Card color={CardColors.LEVEL_2} className="my-0">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <SectionIcon icon={faShapes} />
                      <div className="flex flex-col gap-1">
                        <Text as={TypographyStyles.H1}>Other</Text>
                        <Text as={TypographyStyles.BODY_SM}>
                          These transactions aren&apos;t factored into your net
                          profit.
                        </Text>
                      </div>
                    </div>
                    <SectionTotal
                      label="Total"
                      value={formatCurrency(otherObj.sum ?? 0)}
                      valueColor={TextColors.HEADER}
                    />
                  </div>
                  <div className="flex flex-col pl-12">
                    {otherObj.otherExpenses?.map((other) => (
                      <LineItemRow
                        key={other.transactionCategoryId}
                        categoryId={other.transactionCategoryId}
                        amount={other.sum}
                        labelVariant={TypographyStyles.BODY_SM}
                        labelColor={TextColors.DEFAULT}
                      />
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Guarantees blank space, not the last row's content, is what's at risk of
      getting clipped at the very bottom of the captured content for PDF export */}
      <div style={{ height: 40 }} />
    </div>
  )
}

export default ProfitAndLossPDF
